import type { MedcallReportMedication, MedcallReportParticipant } from '@/pages/medcall/MedcallParticipantReports';
import type { ProfileValue } from './types';

export interface ProfileReportRow { key: string; values: ProfileValue[] }
export interface ProfileMedcallReport {
  from: string | null; to: string | null; identity: ProfileValue[] | null;
  medications: (ProfileReportRow & { timelines: ProfileReportRow[] })[] | null; statuses: ProfileReportRow[] | null;
}
const valueMap = (values: ProfileValue[]) => Object.fromEntries(values.map(field => [field.name, field.value]));
const boolean = (value: string | null | undefined): boolean => {
  if (value == null || value === '0') return false;
  if (value === '1') return true;
  throw new Error('A clinical value needs review before reporting.');
};
const array = (value: string | null | undefined): unknown[] => {
  if (!value) return [];
  let parsed: unknown;
  try { parsed = JSON.parse(value); } catch { throw new Error('A clinical value needs review before reporting.'); }
  if (!Array.isArray(parsed)) throw new Error('A clinical value needs review before reporting.');
  return parsed;
};
const injections = (value: string | null | undefined) => array(value).map(entry => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('A clinical value needs review before reporting.');
  const date = (entry as Record<string, unknown>).date_given;
  if (date != null && typeof date !== 'string') throw new Error('A clinical value needs review before reporting.');
  return { date_given: typeof date === 'string' ? date : null };
});

/** Adapt current shared values for the existing, pure clinical report builders. */
export const profileReportParticipant = (report: ProfileMedcallReport): MedcallReportParticipant => {
  if (!report.identity || !report.medications || !report.statuses) throw new Error('The MedCall report is unavailable.');
  const identity = valueMap(report.identity);
  const medications: MedcallReportMedication[] = report.medications.map(row => {
    const values = valueMap(row.values);
    const weekdays = array(values.days_of_week);
    if (weekdays.some(day => typeof day !== 'string')) throw new Error('A clinical value needs review before reporting.');
    const importance = values.importance_level == null ? null : Number(values.importance_level);
    if (importance !== null && (!Number.isInteger(importance) || importance < 0 || importance > 2)) throw new Error('A clinical value needs review before reporting.');
    return { medicine_name: values.medicine_name, strength: values.strength, route: values.route, frequency: values.frequency,
      times_per_day: values.times_per_day, days_of_week: weekdays as string[], importance_level: importance,
      active: boolean(values.active), prn: boolean(values.prn), injection_info: injections(values.injection_info),
      timelines: row.timelines.map(timeline => {
        const values = valueMap(timeline.values);
        return { date: values.date, status: values.status, route: values.route, injection_info: injections(values.injection_info) };
      }),
    };
  });
  return { firstname: identity.firstname, lastname: identity.lastname, dob: identity.dob, program: identity.program, medications,
    project_sites_medication_statuses: report.statuses.map(row => { const values = valueMap(row.values); return { date: values.date, status: values.status }; }),
  };
};
